import { useParams } from 'react-router-dom';
import { useInvitation } from '@/hooks/useInvitation';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { HiroMark } from '@/components/auth/AuthMarketingPanel';
import { Briefcase, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { PageSeo } from '@/components/seo/PageSeo';
import { NOINDEX_FOLLOW } from '@/lib/seo/constants';
import { invitationSeo } from '@/lib/seo/authSeo';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function permissionLabel(permission: string, isIt: boolean): string {
  if (isIt) {
    switch (permission) {
      case 'admin': return 'Amministratore';
      case 'write': return 'Collaboratore';
      case 'read': return 'Lettore';
      default: return permission;
    }
  }
  switch (permission) {
    case 'admin': return 'Administrator';
    case 'write': return 'Collaborator';
    case 'read': return 'Reader';
    default: return permission;
  }
}

function memberTypeLabel(type: string, isIt: boolean): string {
  if (isIt) return type === 'internal' ? 'Interno' : 'Esterno';
  return type === 'internal' ? 'Internal' : 'External';
}

function formatExpiresAt(raw: { _seconds: number } | string, isIt: boolean): string {
  const date = typeof raw === 'string'
    ? new Date(raw)
    : new Date((raw as { _seconds: number })._seconds * 1000);
  return date.toLocaleDateString(isIt ? 'it-IT' : 'en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// InvitationPage
// ---------------------------------------------------------------------------

/**
 * InvitationPage — public page at /i/:invitationId/:token
 *
 * Sprint 1 (D-R3.3 / BUG-10) — Branding refresh:
 *   - HiroMatch wordmark + HiroMark instead of generic Briefcase + lowercase
 *     "hiromatch" string. Aligns with the rest of the FE (LoginPage,
 *     SignupPage, AdminLayout) which all use the capitalized brand.
 *   - Copy localized via `useLanguage()` so the invitee sees IT or EN
 *     according to their browser-driven app language. We do NOT take the
 *     locale from the inviter here: the invitee may speak a different
 *     language. (The email body is still resolved BE-side from the inviter's
 *     `preferredLanguage` for now — see `SendInvitationEmailExecutor`.)
 *
 * States:
 *   loading           → spinner
 *   invalid / error   → error card
 *   expired           → expired card
 *   already_responded → status card
 *   pending           → action card (Accept / Decline)
 *   declined          → declined confirmation
 *   accepted          → accepted confirmation + dashboard button
 *
 * Auth flow:
 *   - Decline: always public, no auth needed
 *   - Accept (not authenticated): redirects to /login?redirect=/i/:id/:token
 *   - Accept (authenticated): calls /complete directly, verifies email server-side
 */
const InvitationPage = () => {
  const { invitationId, token } = useParams<{ invitationId: string; token: string }>();
  const { language } = useLanguage();
  const isIt = language === 'it';

  const hook = useInvitation(invitationId ?? '', token ?? '');
  const inviteSeo = invitationSeo(language);
  const invitePath = invitationId && token ? `/i/${invitationId}/${token}` : '/i';

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------
  if (hook.pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <PageSeo title={inviteSeo.title} path={invitePath} robots={NOINDEX_FOLLOW} />
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Invalid token
  // ---------------------------------------------------------------------------
  if (hook.pageState === 'invalid' || hook.pageState === 'error') {
    return (
      <PageShell isIt={isIt} seoTitle={inviteSeo.title} seoPath={invitePath}>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <CardTitle className="text-lg">
              {isIt ? 'Link non valido' : 'Invalid link'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {hook.errorMessage ?? (isIt
                ? 'Il link di invito non è valido o è già stato utilizzato.'
                : 'This invitation link is invalid or has already been used.')}
            </p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ---------------------------------------------------------------------------
  // Expired
  // ---------------------------------------------------------------------------
  if (hook.pageState === 'expired') {
    return (
      <PageShell isIt={isIt} seoTitle={inviteSeo.title} seoPath={invitePath}>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <Clock className="h-10 w-10 text-amber-500 mx-auto" />
            <CardTitle className="text-lg">
              {isIt ? 'Invito scaduto' : 'Invitation expired'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {isIt
                ? "Questo link di invito è scaduto. Contatta l'amministratore per ricevere un nuovo invito."
                : 'This invitation link has expired. Contact the administrator to request a new one.'}
            </p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ---------------------------------------------------------------------------
  // Already responded
  // ---------------------------------------------------------------------------
  if (hook.pageState === 'already_responded') {
    return (
      <PageShell isIt={isIt} seoTitle={inviteSeo.title} seoPath={invitePath}>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <CheckCircle className="h-10 w-10 text-muted-foreground mx-auto" />
            <CardTitle className="text-lg">
              {isIt ? 'Hai già risposto' : 'Already responded'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {isIt
                ? 'Hai già risposto a questo invito.'
                : 'You have already responded to this invitation.'}
            </p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ---------------------------------------------------------------------------
  // Declined
  // ---------------------------------------------------------------------------
  if (hook.pageState === 'declined') {
    return (
      <PageShell isIt={isIt} seoTitle={inviteSeo.title} seoPath={invitePath}>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <XCircle className="h-10 w-10 text-muted-foreground mx-auto" />
            <CardTitle className="text-lg">
              {isIt ? "Hai rifiutato l'invito" : 'You declined the invitation'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {isIt
                ? "Puoi contattare l'amministratore se hai cambiato idea."
                : 'You can contact the administrator if you change your mind.'}
            </p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ---------------------------------------------------------------------------
  // Accepted
  // ---------------------------------------------------------------------------
  if (hook.pageState === 'accepted') {
    const companyName = hook.invitationData?.companyPublicName ?? hook.invitationData?.companyName ?? (isIt ? 'il team' : 'the team');
    return (
      <PageShell isIt={isIt} seoTitle={inviteSeo.title} seoPath={invitePath}>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
            <CardTitle className="text-lg">
              {isIt ? `Benvenuto in ${companyName}!` : `Welcome to ${companyName}!`}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {isIt
                ? "Il tuo account è stato configurato. Puoi accedere a HiroMatch."
                : 'Your account has been set up. You can now sign in to HiroMatch.'}
            </p>
            <Button asChild className="mt-2">
              <a href="/dashboard">{isIt ? 'Vai alla dashboard' : 'Go to dashboard'}</a>
            </Button>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ---------------------------------------------------------------------------
  // Pending — main invitation card
  // ---------------------------------------------------------------------------
  const data = hook.invitationData!;
  const roleLabel = data.jobRole ?? permissionLabel(data.permission, isIt);

  return (
    <PageShell isIt={isIt} seoTitle={inviteSeo.title} seoPath={invitePath}>
      <Card className="border-border/50 shadow-sm">
        {/* Header */}
        <CardHeader className="text-center pb-2">
          {data.companyLogoUrl ? (
            <img
              src={data.companyLogoUrl}
              alt={`${data.companyPublicName} logo`}
              className="h-14 w-auto mx-auto mb-3 object-contain"
            />
          ) : (
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-3">
              <Briefcase className="h-6 w-6 text-primary-foreground" />
            </div>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100 mx-auto">
            {isIt ? '✉ Invito ricevuto' : '✉ Invitation received'}
          </span>
          <CardTitle className="text-xl mt-2">
            {isIt
              ? `Sei invitato a unirti a ${data.companyPublicName}`
              : `You're invited to join ${data.companyPublicName}`}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {isIt
              ? `${data.invitedByEmail} ti ha invitato su HiroMatch`
              : `${data.invitedByEmail} invited you to HiroMatch`}
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Role card */}
          <div className="rounded-lg border bg-secondary/30 p-4 space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-primary">
              {isIt ? 'Ruolo offerto' : 'Role offered'}
            </p>
            <p className="text-lg font-bold">{roleLabel}</p>
            <p className="text-sm text-muted-foreground">
              {isIt ? 'Accesso' : 'Access'}: <strong>{permissionLabel(data.permission, isIt)}</strong>
              &nbsp;·&nbsp;
              {isIt ? 'Tipo' : 'Type'}: <strong>{memberTypeLabel(data.type, isIt)}</strong>
            </p>
          </div>

          {/* Expiry */}
          <p className="text-xs text-muted-foreground text-center">
            {isIt ? 'Invito valido fino al' : 'Invitation valid until'}{' '}
            <strong className="text-foreground">{formatExpiresAt(data.expiresAt, isIt)}</strong>
          </p>

          {/* Server error */}
          {hook.errorMessage && (
            <p className="text-sm text-destructive text-center">{hook.errorMessage}</p>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              className="w-full"
              onClick={hook.handleAccept}
              disabled={hook.isSubmitting}
            >
              {hook.isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isIt ? "Accetta l'invito" : 'Accept invitation'}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={hook.handleDecline}
              disabled={hook.isSubmitting}
            >
              {isIt ? 'Rifiuta' : 'Decline'}
            </Button>
          </div>
        </CardContent>
      </Card>

    </PageShell>
  );
};

// ---------------------------------------------------------------------------
// PageShell — consistent outer wrapper with HiroMatch brand header + footer.
// Sprint 1 (D-R3.3 / BUG-10): swapped the legacy Briefcase + "hiromatch" mark
// for the official HiroMark + "HiroMatch" wordmark used elsewhere in the app.
// The "powered by" footer now lives here so every state (pending/error/
// expired/declined/accepted) shows consistent branding — previously only the
// `pending` card carried it, leaving error states unbranded.
// ---------------------------------------------------------------------------

function PageShell({
  children,
  isIt,
  seoTitle,
  seoPath,
}: {
  children: React.ReactNode;
  isIt: boolean;
  seoTitle: string;
  seoPath: string;
}) {
  return (
    <div className="min-h-screen bg-secondary/30 px-4 py-12">
      <PageSeo title={seoTitle} path={seoPath} robots={NOINDEX_FOLLOW} />
      <div className="w-full max-w-md mx-auto space-y-4">
        {/* Brand header */}
        <a
          href="/"
          className="flex items-center justify-center gap-3 mb-8 group"
          aria-label={isIt ? 'Vai alla home di HiroMatch' : 'Go to HiroMatch home'}
        >
          <HiroMark className="h-9 w-9 transition-transform group-hover:scale-105" />
          <span className="font-display font-bold text-2xl tracking-tight">
            HiroMatch
          </span>
        </a>
        {children}
        {/* powered-by footer — always visible across every page state */}
        <p className="text-center text-xs text-muted-foreground/70 mt-6">
          {isIt ? 'powered by' : 'powered by'} <strong>HiroMatch</strong>
        </p>
      </div>
    </div>
  );
}

export default InvitationPage;
